import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { TorchSvg } from '@/components/torch/TorchSvg';
import { useAuthor } from '@/hooks/useAuthor';
import { useProfileTorches } from '@/hooks/useTorchQueries';
import { genUserName } from '@/lib/genUserName';
import type { ParsedChain, AcceptLink } from '@/lib/torchTypes';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, Globe, Zap, Clock, Flame } from 'lucide-react';
import { nip19 } from 'nostr-tools';
import { useEffect, useState } from 'react';

// ─── Mini countdown (same logic as TorchDetail) ───────────────────────────

function LiveCountdown({ targetTs }: { targetTs: number }) {
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));
  useEffect(() => {
    const id = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
    return () => clearInterval(id);
  }, []);
  const remaining = Math.max(0, targetTs - now);
  if (remaining === 0) return <span className="text-yellow-600 font-semibold text-xs">Dormant</span>;
  const d = Math.floor(remaining / 86400);
  const h = Math.floor((remaining % 86400) / 3600);
  const m = Math.floor((remaining % 3600) / 60);
  const s = remaining % 60;
  if (d > 0) return <span className="text-orange-600 font-mono font-semibold text-xs">{d}d {h}h {m}m</span>;
  if (h > 0) return <span className="text-orange-500 font-mono font-semibold text-xs">{h}h {m}m {s}s</span>;
  return <span className="text-red-500 font-mono font-bold text-xs animate-pulse">{m}m {s}s</span>;
}

// ─── Holder mini ──────────────────────────────────────────────────────────

function CurrentHolderMini({ pubkey }: { pubkey: string }) {
  const { data: author } = useAuthor(pubkey);
  const metadata = author?.metadata;
  const displayName = metadata?.name ?? metadata?.display_name ?? genUserName(pubkey);
  const picture = metadata?.picture;
  const initials = displayName.slice(0, 2).toUpperCase();
  return (
    <div className="flex items-center gap-2 mt-2">
      <Avatar className="w-5 h-5 ring-1 ring-orange-400 ring-offset-1">
        <AvatarImage src={picture} alt={displayName} />
        <AvatarFallback className="text-[9px] font-semibold">{initials}</AvatarFallback>
      </Avatar>
      <span className="text-xs text-muted-foreground truncate">
        <span className="text-muted-foreground/60">Held by </span>
        <span className="font-medium text-foreground">{displayName}</span>
      </span>
    </div>
  );
}

// ─── Torch card (self-contained, same pattern as Index) ───────────────────

function ProfileTorchCard({ parsedChain }: { parsedChain: ParsedChain }) {
  const { genesis } = parsedChain;
  const state = parsedChain.state;
  const totalHolders = parsedChain.chainLength + 1;
  const currentHolderPubkey = parsedChain.currentHolder ?? genesis.pubkey;

  const chain: AcceptLink[] = useMemo(() =>
    parsedChain.canonicalChain.map(link => ({
      holderPubkey: link.pass.recipientPubkey,
      zapAmountSats: link.pass.zapAmountSats,
      positionIndex: link.position,
    })),
    [parsedChain]
  );

  const stateBadge = {
    lit:          { label: '🔥 Lit',  className: 'bg-orange-500' },
    extinguished: { label: '🪦 Gone', className: 'bg-gray-500'   },
  }[state];

  return (
    <Link to={`/torch/${genesis.slug}`}>
      <Card className="hover:shadow-lg transition-all duration-300 cursor-pointer h-full group">
        <CardContent className="p-5">
          <div className="flex items-start justify-between mb-3 gap-2">
            <h3 className="text-base font-semibold group-hover:text-orange-500 transition-colors leading-tight line-clamp-2">
              {genesis.title}
            </h3>
            <Badge className={`${stateBadge.className} text-white text-xs shrink-0`}>
              {stateBadge.label}
            </Badge>
          </div>

          <div className="flex justify-center mb-3">
            <TorchSvg
              genesisId={genesis.id}
              chain={chain}
              state={state}
              className="w-28 h-36 group-hover:scale-105 transition-transform duration-500"
            />
          </div>

          <CurrentHolderMini pubkey={currentHolderPubkey} />

          <div className="flex items-center justify-between text-xs text-muted-foreground mt-2">
            <span>{totalHolders} holder{totalHolders !== 1 ? 's' : ''}</span>
            {state === 'lit' && (
              <div className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                <LiveCountdown targetTs={parsedChain.expiresAt} />
              </div>
            )}
            {state === 'extinguished' && <span className="text-gray-400">Sealed</span>}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

// ─── Skeleton for torch card ──────────────────────────────────────────────

function TorchCardSkeleton() {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-3">
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-5 w-14" />
        </div>
        <div className="flex justify-center mb-3">
          <Skeleton className="w-28 h-36" />
        </div>
        <div className="flex items-center gap-2 mt-2">
          <Skeleton className="w-5 h-5 rounded-full" />
          <Skeleton className="h-3 w-24" />
        </div>
        <div className="flex justify-between mt-2">
          <Skeleton className="h-3 w-16" />
          <Skeleton className="h-3 w-16" />
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Main ProfilePage ─────────────────────────────────────────────────────

interface ProfilePageProps {
  pubkey: string;
}

export function ProfilePage({ pubkey }: ProfilePageProps) {
  const { data: author, isLoading: authorLoading } = useAuthor(pubkey);
  const { data: torches, isLoading: torchesLoading } = useProfileTorches(pubkey);

  const metadata = author?.metadata;
  const displayName = metadata?.name ?? metadata?.display_name ?? genUserName(pubkey);
  const picture = metadata?.picture;
  const banner = metadata?.banner;
  const about = metadata?.about;
  const website = metadata?.website;
  const nip05 = metadata?.nip05;
  const lud16 = metadata?.lud16;
  const initials = displayName.slice(0, 2).toUpperCase();

  const npub = useMemo(() => {
    try { return nip19.npubEncode(pubkey); } catch { return ''; }
  }, [pubkey]);

  const litTorches    = useMemo(() => (torches ?? []).filter(c => c.state === 'lit'), [torches]);
  const sealedTorches = useMemo(() => (torches ?? []).filter(c => c.state === 'extinguished'), [torches]);

  // Stats
  const torchesLit  = useMemo(() =>
    (torches ?? []).filter(c => c.genesis.pubkey === pubkey).length,
    [torches, pubkey]
  );
  const torchesHeld = useMemo(() =>
    (torches ?? []).filter(c =>
      c.canonicalChain.some(cp =>
        cp.pass.pubkey === pubkey || cp.pass.recipientPubkey === pubkey
      )
    ).length,
    [torches, pubkey]
  );

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-8">

        {/* Back */}
        <button
          onClick={() => history.back()}
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        {/* Profile header */}
        <div>
          {/* Banner + avatar wrapper — overflow visible so avatar can bleed out */}
          <div className="relative">
            {/* Banner */}
            <div className="rounded-xl overflow-hidden h-36 md:h-48 bg-gradient-to-br from-orange-950/60 to-card">
              {banner && (
                <img
                  src={banner}
                  alt=""
                  className="w-full h-full object-cover"
                />
              )}
              {/* Bottom fade */}
              <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-background/60 to-transparent rounded-b-xl" />
            </div>

            {/* Avatar — absolutely positioned over the banner bottom edge */}
            <div className="absolute -bottom-10 left-2 z-10">
              {authorLoading ? (
                <Skeleton className="w-20 h-20 rounded-full ring-4 ring-background" />
              ) : (
                <Avatar className="w-20 h-20 ring-4 ring-background shadow-lg">
                  <AvatarImage src={picture} alt={displayName} />
                  <AvatarFallback className="text-2xl font-bold">{initials}</AvatarFallback>
                </Avatar>
              )}
            </div>
          </div>

          {/* Name row — sits below the banner, padded to clear the avatar */}
          <div className="mt-12 px-2">
            {authorLoading ? (
              <><Skeleton className="h-6 w-36 mb-1" /><Skeleton className="h-4 w-24" /></>
            ) : (
              <>
                <h1 className="text-xl font-bold leading-tight">{displayName}</h1>
                {nip05 && <p className="text-sm text-orange-400">{nip05}</p>}
                {!nip05 && <p className="text-xs text-muted-foreground font-mono">{npub.slice(0, 16)}…</p>}
              </>
            )}
          </div>

          {/* Bio + links */}
          <div className="mt-4 space-y-2 px-2">
            {authorLoading ? (
              <><Skeleton className="h-4 w-full" /><Skeleton className="h-4 w-3/4" /></>
            ) : (
              <>
                {about && (
                  <p className="text-sm text-foreground/80 leading-relaxed whitespace-pre-wrap">{about}</p>
                )}
                <div className="flex flex-wrap items-center gap-3 mt-2">
                  {website && (
                    <a
                      href={website.startsWith('http') ? website : `https://${website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-orange-400 transition-colors"
                    >
                      <Globe className="w-3.5 h-3.5" />
                      {website.replace(/^https?:\/\//, '')}
                    </a>
                  )}
                  {lud16 && (
                    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Zap className="w-3.5 h-3.5 text-yellow-500" />
                      {lud16}
                    </span>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: 'Torches Lit',  value: torchesLoading ? '…' : torchesLit },
            { label: 'Torches Held', value: torchesLoading ? '…' : torchesHeld },
            { label: 'Total',        value: torchesLoading ? '…' : (torches ?? []).length },
          ].map(({ label, value }) => (
            <Card key={label}>
              <CardContent className="py-4 text-center">
                <div className="text-2xl font-bold">{value}</div>
                <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Torch grid */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Flame className="w-5 h-5 text-orange-400" />
            <h2 className="text-lg font-semibold">Torches</h2>
          </div>

          {torchesLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {[1, 2, 3].map(i => <TorchCardSkeleton key={i} />)}
            </div>
          ) : (torches ?? []).length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-12 text-center">
                <Flame className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">
                  No torches found for this user.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {litTorches.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-orange-400 uppercase tracking-wide mb-3">🔥 Lit</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                    {litTorches.map(tc => (
                      <ProfileTorchCard key={tc.genesis.slug} parsedChain={tc} />
                    ))}
                  </div>
                </div>
              )}
              {sealedTorches.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">🪦 Sealed</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                    {sealedTorches.map(tc => (
                      <ProfileTorchCard key={tc.genesis.slug} parsedChain={tc} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

      </div>
    </Layout>
  );
}
